// Shared types for AI Image Generation components

export type JobState = 'idle' | 'queued' | 'running' | 'completed' | 'error' | 'stopped';

export type ProgressPhase = 'idle' | 'generating-chars' | 'generating-images' | 'completed' | 'error';

export type JobType = 'bulk_creation' | 'additional_images' | 'creator_images' | 'regenerate_image';

export type ImageType = 'profile' | 'gallery_normal' | 'gallery_porn' | 'chat';

export type ApprovalStatus = 'pending' | 'approved' | 'rejected';

export interface GeneratedImage {
  id: string;
  type: ImageType;
  url: string;
  status: ApprovalStatus;
  prompt: string;
  isAdult: boolean;
  isProfileImage: boolean;
}

export interface CharacterAttribute {
  label: string;
  value: string | number | string[];
  type: 'text' | 'number' | 'select' | 'multiselect' | 'date';
}

export interface GeneratedCharacter {
  id: string;
  name: string;
  age: number;
  description: string;
  personality: string;
  gender?: string;
  status: ApprovalStatus;
  images: GeneratedImage[];
  attributes?: Record<string, CharacterAttribute>;
}

export interface AIJobProgress {
  phase: ProgressPhase;
  currentCharacter: number;
  totalCharacters: number;
  currentImage: number;
  totalImages: number;
  errors: string[];
  warnings: string[];
}

export interface AIJob {
  id: string;
  type: JobType;
  state: JobState;
  progress: AIJobProgress;
  characterId?: number;
  characterName?: string;
  startedAt: Date;
  generatedImages: GeneratedImage[];
  generatedCharacters: GeneratedCharacter[];
}

export interface AIGenerationSettings {
  // Character settings (for bulk creation)
  characterCount: number;
  ageMin: number;
  ageMax: number;
  gender: 'male' | 'female' | 'trans';
  bodyTypes: string[];
  ethnicities: string[];

  // Image settings
  totalImages: number;
  adultImagePercent: number;
  adultImagePercentMin?: number;
  adultImagePercentMax?: number;
  galleryNormalPercentMin: number;
  galleryNormalPercentMax: number;
  galleryAdultPercentMin: number;
  galleryAdultPercentMax: number;
  selfiePercentDating?: number;
  selfiePercentExplicit?: number;

  // Video generation settings
  enableVideoGeneration?: boolean;
  videoDuration?: number;  // 5 or 10 seconds
  videoResolution?: string;  // "480p", "720p", "1080p"

  // Personality distribution (dirty/erotic vs clean/romantic)
  personalityDirtyPercentMin?: number;  // Min % of characters with erotic personality
  personalityDirtyPercentMax?: number;  // Max % of characters with erotic personality

  // Creator mode flag: mirrors the selected image profile's creator_compatible flag.
  // DEPRECATED: Use imageProfileId instead — kept for backwards compatibility of cached settings.
  createAsCreator?: boolean;

  // Image Generation Profile: Overrides platform settings with profile configuration
  imageProfileId?: number;

  // Attractiveness range for character generation
  attractivenessMin?: number;
  attractivenessMax?: number;

  // Additional prompts
  customPrompts: string;
  appearanceModifiers: string[];
}

// API response types
export interface ProgressApiPayload {
  phase: ProgressPhase;
  current_character?: number;
  total_characters?: number;
  current_image?: number;
  total_images?: number;
  errors?: string[];
  warnings?: string[];
}

export interface AiJobStatusEvent {
  type: 'ai_job_status';
  job_id: string;
  job_type: JobType;
  state: 'queued' | 'started' | 'progress' | 'completed' | 'error';
  progress?: ProgressApiPayload;
  error?: string;
  timestamp?: string;
  generated_content?: {
    images?: Array<{
      id: string | number;
      type?: string;
      category?: string;
      url: string;
      prompt?: string;
      is_fsk18?: boolean;
      is_adult?: boolean;
    }>;
  };
  metadata?: Record<string, unknown>;
}

export interface AiBulkStatusEvent {
  type: 'ai_bulk_status';
  job_id?: string;
  state: 'started' | 'progress' | 'character_created' | 'completed' | 'error' | 'stopped';
  progress?: ProgressApiPayload;
  metadata?: Record<string, unknown>;
  timestamp?: string;
}

// Default settings
export const DEFAULT_BULK_SETTINGS: AIGenerationSettings = {
  characterCount: 10,
  ageMin: 18,
  ageMax: 35,
  gender: 'female',
  bodyTypes: ['athletic', 'petite', 'curvy', 'slim'],
  ethnicities: ['european', 'mediterranean', 'asian', 'latin', 'african', 'mixed', 'turkish'],
  totalImages: 15,
  adultImagePercent: 70, // Fallback if min/max not set
  adultImagePercentMin: 60, // Min erotic percentage - randomly picked per character
  adultImagePercentMax: 85, // Max erotic percentage - randomly picked per character
  galleryNormalPercentMin: 50,
  galleryNormalPercentMax: 80,
  galleryAdultPercentMin: 10,
  galleryAdultPercentMax: 20,
  selfiePercentDating: 100,  // Changed from 60 to 100
  selfiePercentExplicit: 100,  // Changed from 50 to 100
  // Video generation - disabled by default
  enableVideoGeneration: false,
  videoDuration: 5,
  videoResolution: "720p",
  // Personality distribution: 70-90% erotic (dirty), rest clean (romantic)
  personalityDirtyPercentMin: 70,
  personalityDirtyPercentMax: 90,
  // Creator mode flag — hydrated from the selected image profile's creator_compatible flag.
  createAsCreator: false,
  attractivenessMin: 4,
  attractivenessMax: 8,
  customPrompts: '',
  appearanceModifiers: ['realistic', 'glamorous', 'heavily-made-up'],  // Pre-selected good defaults
};

export const DEFAULT_ADDITIONAL_SETTINGS: AIGenerationSettings = {
  ...DEFAULT_BULK_SETTINGS,
  characterCount: 1,
  totalImages: 5,
};

export const DEFAULT_CREATOR_SETTINGS: AIGenerationSettings = {
  ...DEFAULT_BULK_SETTINGS,
  characterCount: 1,
  totalImages: 8,
  adultImagePercent: 100,
  galleryNormalPercentMin: 0,
  galleryNormalPercentMax: 0,
  galleryAdultPercentMin: 20,
  galleryAdultPercentMax: 30,
};

export const INITIAL_PROGRESS: AIJobProgress = {
  phase: 'idle',
  currentCharacter: 0,
  totalCharacters: 0,
  currentImage: 0,
  totalImages: 0,
  errors: [],
  warnings: [],
};

// Helper functions
export function isAiJobStatusEvent(value: unknown): value is AiJobStatusEvent {
  return Boolean(
    value && typeof value === 'object' && (value as { type?: string }).type === 'ai_job_status'
  );
}

export function isAiBulkStatusEvent(value: unknown): value is AiBulkStatusEvent {
  return Boolean(
    value && typeof value === 'object' && (value as { type?: string }).type === 'ai_bulk_status'
  );
}

export function mapProgressPayload(payload: Partial<ProgressApiPayload>, prev: AIJobProgress): AIJobProgress {
  return {
    phase: (payload.phase ?? prev.phase),
    currentCharacter: payload.current_character ?? prev.currentCharacter,
    totalCharacters: payload.total_characters ?? prev.totalCharacters,
    currentImage: payload.current_image ?? prev.currentImage,
    totalImages: payload.total_images ?? prev.totalImages,
    errors: Array.isArray(payload.errors) ? payload.errors : prev.errors,
    warnings: Array.isArray(payload.warnings) ? payload.warnings : prev.warnings,
  };
}

export function normalizeImageType(category: unknown): ImageType {
  const value = typeof category === 'string'
    ? category.toLowerCase()
    : String((category as { value?: string })?.value ?? '').toLowerCase();

  if (value === 'creator' || value === 'gallery_porn' || value === 'adult' || value === 'nsfw') {
    return 'gallery_porn';
  }
  if (value === 'profile') return 'profile';
  if (value === 'chat') return 'chat';
  return 'gallery_normal';
}

export function makeAbsoluteUrl(url: string): string {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }
  const base = (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL?.replace('/api/v1', '') ?? '';
  const origin = typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}` : '';
  return `${base || origin}${url}`;
}
